import { useQuery } from "@tanstack/react-query"

import {
  type AdminTenderCaseDetail,
  type AdminTenderCasesResponse,
  type AdminTenderDerivedStatus,
  type AdminTenderRunsResponse,
  type AdminTenderUsageWindow,
  getAdminTenderCaseDetail,
  getAdminTenderCases,
  getAdminTenderRuns,
  getAdminTenderUsageSummary,
} from "@/api/admin-api"

import { adminQueryKeys } from "../admin-query-keys"
import { FAST_STALE_TIME, GC_TIME } from "./_shared"

export function useAdminTenderUsageSummaryQuery(
  enabled: boolean,
  { days = 30, currency = "USD" }: { days?: number; currency?: string } = {}
) {
  return useQuery<AdminTenderUsageWindow>({
    queryKey: adminQueryKeys.tenderUsageSummary(days, currency),
    queryFn: () => getAdminTenderUsageSummary(days, currency),
    enabled,
    staleTime: FAST_STALE_TIME,
    gcTime: GC_TIME,
    refetchOnWindowFocus: false,
    placeholderData: (previousData) => previousData,
  })
}

export function useAdminTenderRunsQuery(
  status: "all" | AdminTenderDerivedStatus,
  limit: number,
  enabled: boolean
) {
  return useQuery<AdminTenderRunsResponse>({
    queryKey: adminQueryKeys.tenderRuns(status, limit),
    queryFn: () => getAdminTenderRuns(status, limit),
    enabled,
    staleTime: FAST_STALE_TIME,
    gcTime: GC_TIME,
    refetchOnWindowFocus: false,
    placeholderData: (previousData) => previousData,
  })
}

export function useAdminTenderCasesQuery(
  status: "all" | AdminTenderDerivedStatus,
  limit: number,
  enabled: boolean
) {
  return useQuery<AdminTenderCasesResponse>({
    queryKey: adminQueryKeys.tenderCases(status, limit),
    queryFn: () => getAdminTenderCases(status, limit),
    enabled,
    staleTime: FAST_STALE_TIME,
    gcTime: GC_TIME,
    refetchOnWindowFocus: false,
    placeholderData: (previousData) => previousData,
  })
}

export function useAdminTenderCaseDetailQuery(
  caseId: string | null,
  runId: string | null,
  { enabled, currency = "USD" }: { enabled: boolean; currency?: "SEK" | "USD" | "EUR" }
) {
  return useQuery<AdminTenderCaseDetail>({
    // Currency is part of the key: the cost figures in the payload change with
    // it, so a cached SEK response must not be served for a USD request.
    queryKey: [...adminQueryKeys.tenderCaseDetail(caseId, runId), currency],
    queryFn: () => getAdminTenderCaseDetail(caseId!, runId, currency),
    enabled: enabled && !!caseId,
    staleTime: FAST_STALE_TIME,
    gcTime: GC_TIME,
    refetchOnWindowFocus: false,
  })
}
